import { ApolloQueryResult } from '@apollo/client';
import rootSetters from '../actions';
import rootGetters from '../getters';
import * as queryTypes from './graphqlTypes';
import {
  defaultBlurValidityForm,
  defaultBlurValidityStatusForm,
} from './utils';

export const filterValiditiesQuery =
  (searchFilter: SearchFilter) => (dispatch: DispatchType) =>
    new Promise<ApolloQueryResult<FilterValiditiesQuery>>((resolve, reject) => {
      const {
        graphql: { QUERY },
      } = rootSetters(dispatch);

      QUERY<FilterValiditiesQuery>({
        query: queryTypes.FILTER_VALDITIES,
        variables: {
          searchFilter,
        },
      })
        .then(resolve)
        .catch(reject);
    });

export const filterValidities =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<PaginatedResponse<Validity[]>>((resolve, reject) => {
      const {
        validity: { getValidity },
      } = rootGetters(getState());
      const {
        validity: { setValidity },
        validity: { filterValiditiesQuery },
      } = rootSetters(dispatch);
      const searchFilter: SearchFilter = getValidity(
        'filters.validities.filter',
      );
      filterValiditiesQuery(searchFilter)
        .then((result) => {
          const validities = result.data.filterValidities;
          setValidity('filters.validities.data', validities);
          resolve(validities);
        })
        .catch(reject);
    });

export const filterCompleteValidities =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<PaginatedResponse<Validity[]>>((resolve, reject) => {
      const {
        validity: { getValidity },
      } = rootGetters(getState());
      const {
        validity: { filterValiditiesQuery },
      } = rootSetters(dispatch);
      const searchFilter: SearchFilter = getValidity(
        'filters.validities.filter',
      );
      filterValiditiesQuery({ ...searchFilter, page: 1, limit: -1 })
        .then((result) => {
          const validities = result.data.filterValidities;
          resolve(validities);
        })
        .catch(reject);
    });

export const filterBranches =
  (filter: string) => (dispatch: DispatchType, getState: () => any) =>
    new Promise<Branch[]>((resolve, reject) => {
      const {
        validity: { getValidity },
      } = rootGetters(getState());
      const {
        validity: { setValidity },
        user: { filterBranchesQuery },
      } = rootSetters(dispatch);
      const value = filter;
      const searchFilter: SearchFilter = {
        filter: [
          {
            column: 'code',
            value,
            operator: 'LIKE',
            logicalOperator: 'OR',
          },
          {
            column: 'name',
            value,
            operator: 'LIKE',
            logicalOperator: 'OR',
          },
        ],
        orderBy: [],
        limit: 50,
        page: 1,
      };
      filterBranchesQuery(searchFilter)
        .then((result) => {
          const branches = result.data.filterBranches;
          setValidity('filters.branches.data', branches);
          resolve(branches);
        })
        .catch(reject);
    });

export const autocompleteProducts =
  (column: keyof EsProduct, value: string, id?: keyof EsProduct) =>
  (dispatch: DispatchType, getState: () => any) =>
    new Promise<AutocompleteItem[]>((resolve, reject) => {
      const {
        user: { getRuleCondition },
      } = rootGetters(getState());
      const {
        product: { autocompleteEsProductsQuery },
      } = rootSetters(dispatch);

      const productCondition = getRuleCondition('read', 'Product');
      const clientName = productCondition?.clientName?.in?.[0] ?? 0;

      autocompleteEsProductsQuery({
        id,
        column,
        value,
        filter: [
          // {
          //   column: 'clientName',
          //   value: clientName,
          //   logicalOperator: 'AND',
          // },
        ],
        limit: 40,
      })
        .then((result) => {
          const data = result.data;
          resolve(result.data);
        })
        .catch(reject);
    });

export const filterProducts =
  (filter: string, columns?: Array<'barcodeNumber'>) =>
  (dispatch: DispatchType, getState: () => any) =>
    new Promise<PaginatedResponse<EsProduct[]>>((resolve, reject) => {
      const {
        user: { getRuleCondition },
      } = rootGetters(getState());
      const {
        product: { filterEsProductsQuery },
        validity: { setValidity },
      } = rootSetters(dispatch);

      const productCondition = getRuleCondition('read', 'Product');
      const clientName = productCondition?.clientName?.in?.[0] ?? 0;
      const value = filter;
      const searchFilter: SearchFilter = {
        filter: !columns
          ? [
              // {
              //   column: 'clientName',
              //   value: clientName,
              // },
              {
                column: 'barcodeNumber',
                value,
                operator: 'LIKE',
                logicalOperator: 'OR',
              },
              {
                column: 'subProductId',
                value,
                operator: 'LIKE',
                logicalOperator: 'OR',
              },
              {
                column: 'productFullDescription',
                value,
                operator: 'LIKE',
                logicalOperator: 'OR',
              },
              {
                column: 'productShortDescription',
                value,
                operator: 'LIKE',
                logicalOperator: 'OR',
              },
            ]
          : columns.map((column) => ({
              column,
              value,
              operator: 'LIKE',
              logicalOperator: 'OR',
            })),
        orderBy: [],
        limit: 50,
        page: 1,
      };

      filterEsProductsQuery(searchFilter)
        .then((result) => {
          const products = result.data?.filterEsProducts!;
          setValidity('filters.products.data', products);
          resolve(products);
        })
        .catch((error) => {
          reject(error);
        });
    });

export const validateForm =
  (form: FlattenKeys<ValiditiesState['blur'], 2, 1>) =>
  (dispatch: DispatchType, getState: GetStateType) =>
    new Promise<boolean>((resolve, reject) => {
      let isValid = false;

      if (form === 'validity.create') {
        rootSetters(dispatch).validity.setValidity(
          `blur.validity.create`,
          defaultBlurValidityForm(true),
        );
        const messages =
          rootGetters(getState()).validity.getFormMessage('validity.create');
        isValid = !Object.values(messages).some((message) => message !== '');
      }

      if (form === 'validity.update') {
        rootSetters(dispatch).validity.setValidity(
          `blur.validity.update`,
          defaultBlurValidityForm(true),
        );
        const messages =
          rootGetters(getState()).validity.getFormMessage('validity.update');
        isValid = !Object.values(messages).some((message) => message !== '');
      }

      if (form === 'status.create') {
        rootSetters(dispatch).validity.setValidity(
          `blur.status.create`,
          defaultBlurValidityStatusForm(true),
        );
        const messages =
          rootGetters(getState()).validity.getFormMessage('status.create');
        isValid = !Object.values(messages).some((message) => message !== '');
      }

      if (form === 'status.update') {
        rootSetters(dispatch).validity.setValidity(
          `blur.status.update`,
          defaultBlurValidityStatusForm(true),
        );
        const messages =
          rootGetters(getState()).validity.getFormMessage('status.update');
        isValid = !Object.values(messages).some((message) => message !== '');
      }

      if (isValid) {
        resolve(true);
      } else {
        reject(false);
      }
    });

export const createValidityMutation =
  (createValidityInput: ValidityForm) => (dispatch: DispatchType) =>
    new Promise<Validity>((resolve, reject) => {
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      MUTATE<CreateValidityMutation>({
        mutation: queryTypes.CREATE_VALDITY,
        variables: {
          createValidityInput,
        },
      })
        .then((result) => {
          resolve(result.data?.createValidity!);
        })
        .catch(reject);
    });

export const updateValidityMutation =
  (updateValidityInput: ValidityForm & { id: string }) =>
  (dispatch: DispatchType) =>
    new Promise<Validity>((resolve, reject) => {
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      MUTATE<UpdateValidityMutation>({
        mutation: queryTypes.UPDATE_VALDITY,
        variables: {
          updateValidityInput,
        },
      })
        .then((result) => {
          resolve(result.data?.updateValidity!);
        })
        .catch(reject);
    });

export const deleteValiditiesMutation =
  (validityIds: string[]) => (dispatch: DispatchType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      MUTATE<DeleteValiditiesMutation>({
        mutation: queryTypes.DELETE_VALIDITIES,
        variables: {
          validityIds,
        },
      })
        .then((result) => {
          resolve(result.data?.deleteValidities!);
        })
        .catch(reject);
    });

export const deleteValidities =
  (validityIds: string[]) => (dispatch: DispatchType, getState: () => any) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        validity: { getValidity },
      } = rootGetters(getState());

      const {
        validity: { deleteValiditiesMutation },
      } = rootSetters(dispatch);

      deleteValiditiesMutation(validityIds).then(resolve).catch(reject);
    });

export const createValidity =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<Validity>((resolve, reject) => {
      const {
        validity: { getValidity },
      } = rootGetters(getState());

      const {
        validity: { createValidityMutation },
      } = rootSetters(dispatch);

      const createValidityInput = getValidity('forms.validity.create');
      createValidityMutation(createValidityInput).then(resolve).catch(reject);
    });

export const updateValidity =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<Validity>((resolve, reject) => {
      const {
        validity: { getValidity },
      } = rootGetters(getState());

      const {
        validity: { updateValidityMutation },
      } = rootSetters(dispatch);

      const updateValidityInput = getValidity('forms.validity.update');
      updateValidityMutation(updateValidityInput).then(resolve).catch(reject);
    });

export const autocompleteValiditiesQuery =
  (autocompleteFilter: AutocompleteFilter) => (dispatch: DispatchType) =>
    new Promise<AutocompleteResponse>((resolve, reject) => {
      const {
        graphql: { QUERY },
      } = rootSetters(dispatch);
      QUERY<AutocompleteValiditiesQuery>({
        query: queryTypes.AUTOCOMPLETE_VALIDITIES,
        variables: {
          autocompleteFilter,
        },
      })
        .then((result) => {
          resolve(result.data.autocompleteValidities);
        })
        .catch(reject);
    });

export const autocompleteValidities =
  (column: keyof Validity, value: string, id?: keyof Validity) =>
  (dispatch: DispatchType, getState: () => any) =>
    new Promise<AutocompleteItem[]>((resolve, reject) => {
      const {
        user: { getRuleCondition },
      } = rootGetters(getState());
      const {
        validity: { autocompleteValiditiesQuery },
      } = rootSetters(dispatch);

      const productCondition = getRuleCondition('read', 'Product');
      const clientName = productCondition?.clientName?.in?.[0] ?? 0;

      autocompleteValiditiesQuery({
        id,
        column,
        value,
        filter: [],
        limit: 40,
      })
        .then((result) => {
          resolve(result.data);
        })
        .catch(reject);
    });

export const createValidityStatusMutation =
  (createValidityStatusInput: ValidityStatusForm) => (dispatch: DispatchType) =>
    new Promise<ValidityStatus>((resolve, reject) => {
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      MUTATE<CreateValidityStatusMutation>({
        mutation: queryTypes.CREATE_VALDITY_STATUS,
        variables: {
          createValidityStatusInput,
        },
      })
        .then((result) => {
          resolve(result.data?.createValidityStatus!);
        })
        .catch(reject);
    });

export const updateValidityStatusMutation =
  (updateValidityStatusInput: ValidityStatusForm & { id: string }) =>
  (dispatch: DispatchType) =>
    new Promise<ValidityStatus>((resolve, reject) => {
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      MUTATE<UpdateValidityStatusMutation>({
        mutation: queryTypes.UPDATE_VALDITY_STATUS,
        variables: {
          updateValidityStatusInput,
        },
      })
        .then((result) => {
          resolve(result.data?.updateValidityStatus!);
        })
        .catch(reject);
    });

export const filterValidityStatusQuery =
  (searchFilter: SearchFilter) => (dispatch: DispatchType) =>
    new Promise<ApolloQueryResult<FilterValidityStatusQuery>>(
      (resolve, reject) => {
        const {
          graphql: { QUERY },
        } = rootSetters(dispatch);

        QUERY<FilterValidityStatusQuery>({
          query: queryTypes.FILTER_VALDITY_STATUS,
          variables: {
            searchFilter,
          },
        })
          .then(resolve)
          .catch(reject);
      },
    );

export const autocompleteValidityStatusQuery =
  (autocompleteFilter: AutocompleteFilter) => (dispatch: DispatchType) =>
    new Promise<AutocompleteResponse>((resolve, reject) => {
      const {
        graphql: { QUERY },
      } = rootSetters(dispatch);
      QUERY<AutocompleteValidityStatusQuery>({
        query: queryTypes.AUTOCOMPLETE_VALIDITY_STATUS,
        variables: {
          autocompleteFilter,
        },
      })
        .then((result) => {
          resolve(result.data.autocompleteValidityStatus);
        })
        .catch(reject);
    });

export const deleteValidityStatusMutation =
  (validityStatusIds: string[]) => (dispatch: DispatchType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        graphql: { MUTATE },
      } = rootSetters(dispatch);
      MUTATE<DeleteValidityStatusMutation>({
        mutation: queryTypes.DELETE_VALIDITY_STATUS,
        variables: {
          validityStatusIds,
        },
      })
        .then((result) => {
          resolve(result.data?.deleteValidityStatus!);
        })
        .catch(reject);
    });

export const createValidityStatus =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<ValidityStatus>((resolve, reject) => {
      const {
        validity: { getValidity },
      } = rootGetters(getState());
      const {
        validity: { createValidityStatusMutation },
      } = rootSetters(dispatch);

      const createValidityStatusInput = getValidity('forms.status.create');
      createValidityStatusMutation(createValidityStatusInput)
        .then(resolve)
        .catch(reject);
    });

export const updateValidityStatus =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<ValidityStatus>((resolve, reject) => {
      const {
        validity: { getValidity },
      } = rootGetters(getState());

      const {
        validity: { updateValidityStatusMutation },
      } = rootSetters(dispatch);

      const updateValidityStatusInput = getValidity('forms.status.update');
      updateValidityStatusMutation(updateValidityStatusInput)
        .then(resolve)
        .catch(reject);
    });

export const filterValidityStatus =
  () => (dispatch: DispatchType, getState: () => any) =>
    new Promise<PaginatedResponse<ValidityStatus[]>>((resolve, reject) => {
      const {
        validity: { getValidity },
      } = rootGetters(getState());
      const {
        validity: { setValidity },
        validity: { filterValidityStatusQuery },
      } = rootSetters(dispatch);
      const searchFilter: SearchFilter = getValidity('filters.status.filter');
      filterValidityStatusQuery(searchFilter)
        .then((result) => {
          const validityStatus = result.data.filterValidityStatus;
          setValidity('filters.status.data', validityStatus);
          resolve(validityStatus);
        })
        .catch(reject);
    });

export const filterValidityStatusValidity =
  (filter: string) => (dispatch: DispatchType) =>
    new Promise<PaginatedResponse<ValidityStatus[]>>((resolve, reject) => {
      const {
        validity: { setValidity },
        validity: { filterValidityStatusQuery },
      } = rootSetters(dispatch);
      const searchFilter: SearchFilter = {
        filter: [
          // {
          //   column: 'code',
          //   value,
          //   operator: 'LIKE',
          //   logicalOperator: 'OR',
          // },
        ],
        orderBy: [],
        limit: 30,
        page: 1,
      };
      filterValidityStatusQuery(searchFilter)
        .then((result) => {
          const validityStatus = result.data.filterValidityStatus;
          setValidity('filters.validityStatus.data', validityStatus.data);
          resolve(validityStatus);
        })
        .catch(reject);
    });

export const autocompleteValidityStatus =
  (column: keyof ValidityStatus, value: string, id?: keyof ValidityStatus) =>
  (dispatch: DispatchType) =>
    new Promise<AutocompleteItem[]>((resolve, reject) => {
      const {
        validity: { autocompleteValidityStatusQuery },
      } = rootSetters(dispatch);
      autocompleteValidityStatusQuery({
        id,
        column,
        value,
        filter: [],
        limit: 40,
      })
        .then((result) => {
          resolve(result.data);
        })
        .catch(reject);
    });

export const deleteValidityStatus =
  (validityStatusIds: string[]) => (dispatch: DispatchType) =>
    new Promise<BatchResponse>((resolve, reject) => {
      const {
        validity: { deleteValidityStatusMutation },
      } = rootSetters(dispatch);

      deleteValidityStatusMutation(validityStatusIds)
        .then(resolve)
        .catch(reject);
    });

const validityActionCreators = (dispatch: <T>(action: any) => T) =>
  Object.assign(
    {},
    {
      filterProducts: (filter: string) =>
        dispatch<Promise<PaginatedResponse<EsProduct[]>>>(
          filterProducts(filter),
        ),
      validateForm: (form: FlattenKeys<ValiditiesState['blur'], 2, 1>) =>
        dispatch<Promise<PaginatedResponse<EsProduct[]>>>(validateForm(form)),
      autocompleteProducts: (
        column: keyof EsProduct,
        value: string,
        id?: keyof EsProduct,
      ) =>
        dispatch<Promise<AutocompleteItem[]>>(
          autocompleteProducts(column, value, id),
        ),
      filterValiditiesQuery: (searchFilter: SearchFilter) =>
        dispatch<Promise<ApolloQueryResult<FilterValiditiesQuery>>>(
          filterValiditiesQuery(searchFilter),
        ),
      filterValidityStatusQuery: (searchFilter: SearchFilter) =>
        dispatch<Promise<ApolloQueryResult<FilterValidityStatusQuery>>>(
          filterValidityStatusQuery(searchFilter),
        ),
      filterValidities: () =>
        dispatch<Promise<PaginatedResponse<Validity[]>>>(filterValidities()),
      filterValidityStatus: () =>
        dispatch<Promise<PaginatedResponse<ValidityStatus[]>>>(
          filterValidityStatus(),
        ),
      filterValidityStatusValidity: (filter: string) =>
        dispatch<Promise<PaginatedResponse<ValidityStatus[]>>>(
          filterValidityStatusValidity(filter),
        ),
      filterCompleteValidities: () =>
        dispatch<Promise<PaginatedResponse<Validity[]>>>(
          filterCompleteValidities(),
        ),
      filterBranches: (filter: string) =>
        dispatch<Promise<FilterBranchesQuery>>(filterBranches(filter)),
      createValidityMutation: (createValidityInput: ValidityForm) =>
        dispatch<Promise<Validity>>(
          createValidityMutation(createValidityInput),
        ),
      createValidityStatusMutation: (
        createValidityStatusInput: ValidityStatusForm,
      ) =>
        dispatch<Promise<ValidityStatus>>(
          createValidityStatusMutation(createValidityStatusInput),
        ),
      updateValidityMutation: (
        updateValidityInput: ValidityForm & { id: string },
      ) =>
        dispatch<Promise<Validity>>(
          updateValidityMutation(updateValidityInput),
        ),
      updateValidityStatusMutation: (
        updateValidityStatusInput: ValidityStatusForm & { id: string },
      ) =>
        dispatch<Promise<ValidityStatus>>(
          updateValidityStatusMutation(updateValidityStatusInput),
        ),
      createValidity: () => dispatch<Promise<Validity>>(createValidity()),
      createValidityStatus: () =>
        dispatch<Promise<ValidityStatus>>(createValidityStatus()),
      updateValidity: () => dispatch<Promise<Validity>>(updateValidity()),
      updateValidityStatus: () =>
        dispatch<Promise<ValidityStatus>>(updateValidityStatus()),

      deleteValiditiesMutation: (validityIds: string[]) =>
        dispatch<Promise<BatchResponse>>(deleteValiditiesMutation(validityIds)),

      deleteValidityStatusMutation: (validityStatusIds: string[]) =>
        dispatch<Promise<BatchResponse>>(
          deleteValidityStatusMutation(validityStatusIds),
        ),

      deleteValidities: (validityIds: string[]) =>
        dispatch<Promise<BatchResponse>>(deleteValidities(validityIds)),
      deleteValidityStatus: (validityStatusIds: string[]) =>
        dispatch<Promise<BatchResponse>>(
          deleteValidityStatus(validityStatusIds),
        ),
      autocompleteValiditiesQuery: (autocompleteFilter: AutocompleteFilter) =>
        dispatch<Promise<AutocompleteResponse>>(
          autocompleteValiditiesQuery(autocompleteFilter),
        ),
      autocompleteValidityStatusQuery: (
        autocompleteFilter: AutocompleteFilter,
      ) =>
        dispatch<Promise<AutocompleteResponse>>(
          autocompleteValidityStatusQuery(autocompleteFilter),
        ),
      autocompleteValidities: (
        column: keyof Validity,
        value: string,
        id?: keyof Validity,
      ) =>
        dispatch<Promise<AutocompleteItem[]>>(
          autocompleteValidities(column, value, id),
        ),
      autocompleteValidityStatus: (
        column: keyof ValidityStatus,
        value: string,
        id?: keyof ValidityStatus,
      ) =>
        dispatch<Promise<AutocompleteItem[]>>(
          autocompleteValidityStatus(column, value, id),
        ),
    },
  );

export default validityActionCreators;
